import { z } from "zod";
import type { AIServiceDateParams } from "../ai-services/types";

export type CacheOverviewParams = AIServiceDateParams & {
  service_type?: string;
  instance_id?: string[];
};

// A KPI compares against the preceding window of the same length. Both
// `previous` and `delta_pct` are null when that window held no data — a delta
// against zero is noise, not a trend.
export const cacheKpiSchema = z.object({
  value: z.number(),
  previous: z.number().nullable().default(null),
  delta_pct: z.number().nullable().default(null),
});
export type CacheKpi = z.infer<typeof cacheKpiSchema>;

export const cacheModelRowSchema = z.object({
  model: z.string(),
  service_type: z.string(),
  requests: z.number(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  cache_read_tokens: z.number(),
  cache_write_tokens: z.number(),
  read_ratio: z.number(),
  // Null when nothing was written — the ratio is undefined, not zero.
  write_amortization: z.number().nullable().default(null),
});
export type CacheModelRow = z.infer<typeof cacheModelRowSchema>;

export const cachePointSchema = z.object({
  bucket: z.string(),
  model: z.string(),
  input_tokens: z.number(),
  cache_read_tokens: z.number(),
  cache_write_tokens: z.number(),
  read_ratio: z.number(),
  write_amortization: z.number().nullable().default(null),
});
export type CachePoint = z.infer<typeof cachePointSchema>;

export const cacheOverviewResponseSchema = z.object({
  kpis: z.object({
    uncached_input_tokens: cacheKpiSchema,
    cache_read_tokens: cacheKpiSchema,
    cache_read_ratio: cacheKpiSchema,
  }),
  models: z.array(cacheModelRowSchema).default([]),
  series: z.array(cachePointSchema).default([]),
  bucket: z.string(),
  covered_services: z.array(z.string()).default([]),
});
export type CacheOverviewResponse = z.infer<typeof cacheOverviewResponseSchema>;
